import type { TypedArray } from '@kitware/vtk.js/types';

// Keep the working VTK image small enough for several MPR/3D GPU copies while
// retaining every slice of the series. Only X and Y are sampled more sparsely.
export const MOBILE_VOLUME_BUDGET_BYTES = 32 * 1024 * 1024;

export function isMobileBrowser() {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

export function getInPlaneSampleStep(
  width: number,
  height: number,
  slices: number,
  bytesPerVoxel: number,
  maxBytes = MOBILE_VOLUME_BUDGET_BYTES
) {
  if (
    !Number.isFinite(width * height * slices * bytesPerVoxel) ||
    width <= 0 ||
    height <= 0 ||
    slices <= 0 ||
    bytesPerVoxel <= 0 ||
    !Number.isFinite(maxBytes) ||
    maxBytes <= 0
  ) {
    throw new Error('Invalid volume dimensions');
  }
  if (slices * bytesPerVoxel > maxBytes) {
    throw new Error('Volume has too many slices for the memory budget');
  }

  let step = 1;
  while (
    Math.ceil(width / step) *
      Math.ceil(height / step) *
      slices *
      bytesPerVoxel >
    maxBytes
  ) {
    step++;
  }
  return step;
}

export function copyInPlaneSamples(
  source: TypedArray,
  target: TypedArray,
  width: number,
  height: number,
  slices: number,
  components: number,
  step: number,
  targetOffset = 0
) {
  if (
    !Number.isInteger(step) ||
    step < 1 ||
    !Number.isInteger(targetOffset) ||
    targetOffset < 0
  ) {
    throw new Error('Invalid in-plane sampling parameters');
  }
  if (
    source.length < width * height * slices * components ||
    target.length <
      targetOffset +
        Math.ceil(width / step) * Math.ceil(height / step) * slices * components
  ) {
    throw new Error('Pixel buffer is smaller than the sampled volume');
  }
  const targetWidth = Math.ceil(width / step);
  const targetHeight = Math.ceil(height / step);
  let targetIndex = targetOffset;

  for (let z = 0; z < slices; z++) {
    for (let y = 0; y < targetHeight; y++) {
      for (let x = 0; x < targetWidth; x++) {
        const sourceIndex =
          ((z * height + y * step) * width + x * step) * components;
        for (let component = 0; component < components; component++) {
          target[targetIndex++] = source[sourceIndex + component];
        }
      }
    }
  }
}
